import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/utils';

const KANBAN_COLUMNS = [
  { id: 'DRAFT', labelKey: 'kanban.draft', colorKey: 'text.secondary' },
  { id: 'IN_REVIEW', labelKey: 'kanban.inReview', colorKey: 'warning.main' },
  { id: 'PENDING_SIGNATURE', labelKey: 'kanban.pendingSignature', colorKey: 'info.main' },
  { id: 'SIGNED', labelKey: 'kanban.signed', colorKey: 'success.main' },
];

const GOLD_ACCENT = '#c9a96e';

const getNestedColor = (theme, colorKey) => {
  const parts = colorKey.split('.');
  let value = theme.palette;
  for (const p of parts) value = value?.[p];
  return value || '#888';
};

// ─── Drop indicator line ───────────────────────────────────────────────
const DropIndicator = () => (
  <Box sx={{
    height: 2,
    bgcolor: GOLD_ACCENT,
    borderRadius: 1,
    mx: 0.5,
    my: 0.75,
    boxShadow: `0 0 6px ${alpha(GOLD_ACCENT, 0.5)}`,
  }} />
);

// ─── Card ──────────────────────────────────────────────────────────────
const KanbanCard = ({ contract, onContractClick, onDragStart, onDragOver, isDragging }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const collaboratorNames = contract.collaborators
    ? contract.collaborators.slice(0, 3).map(c => `${c.first_name} ${c.last_name}`).join(', ')
    : '';

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart(e, contract)}
      onDragOver={onDragOver}
      onClick={() => onContractClick(contract)}
      sx={{
        p: 2,
        borderRadius: '8px',
        bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : alpha('#000', 0.02),
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        cursor: 'grab',
        transition: 'opacity 0.15s ease, transform 0.15s ease',
        opacity: isDragging ? 0.3 : 1,
        transform: isDragging ? 'scale(0.95)' : 'none',
        '&:hover': {
          borderColor: alpha(GOLD_ACCENT, 0.4),
          boxShadow: `0 2px 8px ${alpha('#000', 0.15)}`,
          transform: isDragging ? 'scale(0.95)' : 'translateY(-1px)',
        },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Typography sx={{
        fontWeight: 600, fontSize: '0.85rem', color: theme.palette.text.primary,
        mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {contract.title}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {contract.type && (
          <Chip
            label={t(`contractCreationType.${contract.type}`, contract.type)}
            size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500,
              bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, border: 'none' }}
          />
        )}
        {contract.country && (
          <Chip
            label={contract.country}
            size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500,
              bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, border: 'none' }}
          />
        )}
      </Box>

      {collaboratorNames && (
        <Typography variant="caption" sx={{
          color: theme.palette.text.secondary, fontSize: '0.72rem',
          display: 'block', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {collaboratorNames}
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: '0.68rem' }}>
        {formatDate(contract.created_at, i18n.language)}
      </Typography>
    </Box>
  );
};

// ─── Board ─────────────────────────────────────────────────────────────
const ContractKanban = ({ contracts, onContractClick, onReorder }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [draggedContract, setDraggedContract] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { columnId, index }

  // Group contracts by status, sorted by kanban_position
  const groupedContracts = useMemo(() => {
    const groups = {};
    KANBAN_COLUMNS.forEach(col => { groups[col.id] = []; });
    (contracts || []).forEach(c => {
      const status = (c.status || 'DRAFT').toUpperCase();
      if (groups[status]) groups[status].push(c);
    });
    // Sort each column by kanban_position
    KANBAN_COLUMNS.forEach(col => {
      groups[col.id].sort((a, b) => (a.kanban_position || 0) - (b.kanban_position || 0));
    });
    return groups;
  }, [contracts]);

  // ── Drag handlers ──────────────────────────────────────────────────

  const handleDragStart = useCallback((e, contract) => {
    setDraggedContract(contract);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', contract.id);
  }, []);

  // Fired when dragging over a card — detects top/bottom half for insert position
  const handleCardDragOver = useCallback((e, columnId, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const insertIndex = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    setDropTarget(prev =>
      prev?.columnId === columnId && prev?.index === insertIndex ? prev : { columnId, index: insertIndex }
    );
  }, []);

  // Fired when dragging over empty space in a column — defaults to end
  const handleColumnDragOver = useCallback((e, columnId, columnLength) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(prev =>
      prev?.columnId === columnId && prev?.index === columnLength ? prev : { columnId, index: columnLength }
    );
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  }, []);

  const handleDrop = useCallback((e, targetColumnId) => {
    e.preventDefault();
    if (!draggedContract) return;

    const sourceColumnId = draggedContract.status.toUpperCase();
    const sourceList = groupedContracts[sourceColumnId] || [];
    const targetList = groupedContracts[targetColumnId] || [];
    const insertIndex = dropTarget?.columnId === targetColumnId
      ? dropTarget.index
      : targetList.length;

    // Snapshot contracts before change (for revert on failure)
    const affectedContracts = sourceColumnId === targetColumnId
      ? [...targetList]
      : [...sourceList, ...targetList];

    let updates;

    if (sourceColumnId === targetColumnId) {
      // ── Same-column reorder ──
      const currentIndex = targetList.findIndex(c => c.id === draggedContract.id);
      const adjustedInsert = currentIndex < insertIndex ? insertIndex - 1 : insertIndex;
      if (currentIndex === adjustedInsert) {
        setDraggedContract(null);
        setDropTarget(null);
        return;
      }
      const reordered = targetList.filter(c => c.id !== draggedContract.id);
      reordered.splice(adjustedInsert, 0, draggedContract);
      updates = reordered.map((c, i) => ({
        id: c.id,
        kanban_position: i,
      }));
    } else {
      // ── Cross-column move ──
      const newSource = sourceList.filter(c => c.id !== draggedContract.id);
      const newTarget = [...targetList];
      const clamped = Math.min(insertIndex, newTarget.length);
      newTarget.splice(clamped, 0, draggedContract);

      updates = [
        ...newSource.map((c, i) => ({ id: c.id, kanban_position: i })),
        ...newTarget.map((c, i) => ({
          id: c.id,
          kanban_position: i,
          ...(c.id === draggedContract.id ? { status: targetColumnId } : {}),
        })),
      ];
    }

    setDraggedContract(null);
    setDropTarget(null);
    onReorder(updates, affectedContracts);
  }, [draggedContract, dropTarget, groupedContracts, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDraggedContract(null);
    setDropTarget(null);
  }, []);

  // ── Drop indicator visibility ──────────────────────────────────────
  const shouldShowIndicator = useCallback((columnId, insertIndex) => {
    if (!draggedContract || !dropTarget || dropTarget.columnId !== columnId || dropTarget.index !== insertIndex) {
      return false;
    }
    const sourceColumnId = draggedContract.status.toUpperCase();
    if (sourceColumnId !== columnId) return true;
    // Same column — hide indicator at current position (no-op drop)
    const columnContracts = groupedContracts[columnId] || [];
    const currentIndex = columnContracts.findIndex(c => c.id === draggedContract.id);
    return insertIndex !== currentIndex && insertIndex !== currentIndex + 1;
  }, [draggedContract, dropTarget, groupedContracts]);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Box sx={{
      display: 'flex', gap: 2, overflowX: 'auto', pb: 2, minHeight: 400,
      '&::-webkit-scrollbar': { height: 6 },
      '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.text.secondary, 0.2), borderRadius: 3 },
    }}>
      {KANBAN_COLUMNS.map((column) => {
        const columnColor = getNestedColor(theme, column.colorKey);
        const columnContracts = groupedContracts[column.id] || [];
        const isDropTarget = dropTarget?.columnId === column.id;

        return (
          <Box
            key={column.id}
            onDragOver={(e) => handleColumnDragOver(e, column.id, columnContracts.length)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
            onDragEnd={handleDragEnd}
            sx={{
              minWidth: 280, maxWidth: 320, flex: '1 0 280px',
              display: 'flex', flexDirection: 'column', borderRadius: '10px',
              bgcolor: alpha('#fff', 0.02),
              border: `1px solid ${isDropTarget ? alpha(GOLD_ACCENT, 0.5) : alpha(theme.palette.divider, 0.5)}`,
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              boxShadow: isDropTarget
                ? `0 0 0 1px ${alpha(GOLD_ACCENT, 0.3)}, inset 0 0 20px ${alpha(GOLD_ACCENT, 0.05)}`
                : 'none',
              overflow: 'hidden',
            }}
          >
            {/* Column Header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: columnColor }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: theme.palette.text.primary, letterSpacing: '0.02em' }}>
                  {t(column.labelKey)}
                </Typography>
              </Box>
              <Chip
                label={columnContracts.length}
                size="small"
                sx={{ height: 22, minWidth: 28, fontSize: '0.72rem', fontWeight: 700,
                  bgcolor: alpha(columnColor, 0.12), color: columnColor, border: 'none' }}
              />
            </Box>

            {/* Cards */}
            <Box sx={{
              flex: 1, p: 1.5, overflowY: 'auto', minHeight: 100,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.text.secondary, 0.15), borderRadius: 2 },
            }}>
              {shouldShowIndicator(column.id, 0) && <DropIndicator />}

              {columnContracts.map((contract, index) => (
                <React.Fragment key={contract.id}>
                  <Box sx={{ mb: 1.5 }}>
                    <KanbanCard
                      contract={contract}
                      onContractClick={onContractClick}
                      onDragStart={handleDragStart}
                      onDragOver={(e) => handleCardDragOver(e, column.id, index)}
                      isDragging={draggedContract?.id === contract.id}
                    />
                  </Box>
                  {shouldShowIndicator(column.id, index + 1) && <DropIndicator />}
                </React.Fragment>
              ))}

              {/* Empty state */}
              {columnContracts.length === 0 && !isDropTarget && (
                <Box sx={{ p: 3, textAlign: 'center', color: alpha(theme.palette.text.secondary, 0.5) }}>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                    {t('kanban.noContracts', 'No contracts')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ContractKanban;
